import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import {
  STOCK_COUNT_ADJUSTMENT_TYPE,
  STOCK_COUNT_ADJUSTMENT_TYPE_LABELS,
  STOCK_COUNT_ITEM_TYPE,
  STOCK_COUNT_ITEM_TYPE_LABELS,
  STOCK_COUNT_SECTOR,
  STOCK_COUNT_SECTOR_LABELS,
} from "../utils/stockCountItemModel";

const fieldRootSx = {
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.50",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 650,
  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
  "& fieldset": { borderColor: "transparent" },
  "&:hover fieldset": { borderColor: "transparent" },
  "&.Mui-focused fieldset": { borderColor: "secondary.500" },
};

const selectSx = {
  width: { xs: "100%", sm: 180 },
  ...fieldRootSx,
  bgcolor: "secondary.150",
  "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0 },
};

export default function StockCountItemsFilters({
  itemType,
  categoryId,
  sector,
  categories = [],
  categoriesLoading = false,
  adjustmentType,
  viewMode = "all",
  search = "",
  disabled = false,
  onItemTypeChange,
  onCategoryChange,
  onSectorChange,
  onAdjustmentTypeChange,
  onViewModeChange,
  onSearchChange,
}) {
  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      spacing={1.25}
      sx={{
        alignItems: { xs: "stretch", lg: "center" },
        justifyContent: "space-between",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Select
          value={itemType}
          onChange={(event) => onItemTypeChange(event.target.value)}
          displayEmpty
          size="small"
          disabled={disabled}
          inputProps={{ "aria-label": "Filtrar por tipo de producto" }}
          sx={selectSx}
          renderValue={(value) =>
            STOCK_COUNT_ITEM_TYPE_LABELS[value] || "Tipo"
          }
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value={STOCK_COUNT_ITEM_TYPE.PURCHASE_ITEM}>
            {STOCK_COUNT_ITEM_TYPE_LABELS.PURCHASE_ITEM}
          </MenuItem>
          <MenuItem value={STOCK_COUNT_ITEM_TYPE.PREPARED_PRODUCT}>
            {STOCK_COUNT_ITEM_TYPE_LABELS.PREPARED_PRODUCT}
          </MenuItem>
        </Select>

        {itemType === STOCK_COUNT_ITEM_TYPE.PURCHASE_ITEM ? (
          <Select
            value={categoryId}
            onChange={(event) => onCategoryChange(event.target.value)}
            displayEmpty
            size="small"
            disabled={disabled || categoriesLoading}
            inputProps={{ "aria-label": "Filtrar por categoría" }}
            sx={selectSx}
            renderValue={(value) =>
              categories.find(
                (category) => String(category.id) === String(value)
              )?.name ||
              (categoriesLoading ? "Cargando categorías..." : "Categoría")
            }
          >
            <MenuItem value="">Todas las categorías</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={String(category.id)}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        ) : itemType === STOCK_COUNT_ITEM_TYPE.PREPARED_PRODUCT ? (
          <Select
            value={sector}
            onChange={(event) => onSectorChange(event.target.value)}
            displayEmpty
            size="small"
            disabled={disabled}
            inputProps={{ "aria-label": "Filtrar por sector" }}
            sx={selectSx}
            renderValue={(value) =>
              STOCK_COUNT_SECTOR_LABELS[value] || "Sector"
            }
          >
            <MenuItem value="">Todos los sectores</MenuItem>
            <MenuItem value={STOCK_COUNT_SECTOR.KITCHEN}>
              {STOCK_COUNT_SECTOR_LABELS.KITCHEN}
            </MenuItem>
            <MenuItem value={STOCK_COUNT_SECTOR.BAR}>
              {STOCK_COUNT_SECTOR_LABELS.BAR}
            </MenuItem>
          </Select>
        ) : (
          <Select
            value=""
            displayEmpty
            size="small"
            disabled
            inputProps={{
              "aria-label":
                "Filtro de categoría o sector disponible al seleccionar un tipo",
            }}
            sx={selectSx}
            renderValue={() => "Categoría / Sector"}
          >
            <MenuItem value="">Categoría / Sector</MenuItem>
          </Select>
        )}

        <Select
          value={adjustmentType}
          onChange={(event) => onAdjustmentTypeChange(event.target.value)}
          displayEmpty
          size="small"
          disabled={disabled}
          inputProps={{ "aria-label": "Filtrar por tipo de ajuste" }}
          sx={selectSx}
          renderValue={(value) =>
            STOCK_COUNT_ADJUSTMENT_TYPE_LABELS[value] || "Tipo de ajuste"
          }
        >
          <MenuItem value="">Todos los ajustes</MenuItem>
          <MenuItem value={STOCK_COUNT_ADJUSTMENT_TYPE.NEGATIVE}>
            {STOCK_COUNT_ADJUSTMENT_TYPE_LABELS.NEGATIVE}
          </MenuItem>
          <MenuItem value={STOCK_COUNT_ADJUSTMENT_TYPE.POSITIVE}>
            {STOCK_COUNT_ADJUSTMENT_TYPE_LABELS.POSITIVE}
          </MenuItem>
          <MenuItem value={STOCK_COUNT_ADJUSTMENT_TYPE.ZERO}>
            {STOCK_COUNT_ADJUSTMENT_TYPE_LABELS.ZERO}
          </MenuItem>
        </Select>
      </Stack>

      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          width: { xs: "100%", lg: "auto" },
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <ToggleButtonGroup
          exclusive
          value={viewMode}
          onChange={(_, value) => value && onViewModeChange(value)}
          size="small"
          disabled={disabled}
          aria-label="Vista de productos contados"
          sx={{
            flexShrink: 0,
            height: 38,
            p: "2px",
            bgcolor: "common.white",
            border: 0,
            borderRadius: "14px",
            gap: "4px",
            boxSizing: "border-box",
            "& .MuiToggleButton-root": {
              px: 1.5,
              minWidth: 132,
              height: 30,
              border: 0,
              borderRadius: "9px !important",
              color: "primary.900",
              fontSize: 13,
              fontWeight: 800,
              textTransform: "none",
              lineHeight: 1.1,
              "&:hover": {
                bgcolor: "rgba(88, 110, 184, 0.08)",
              },
              "&.Mui-selected": {
                bgcolor: "secondary.500",
                color: "logoblanco",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
                "&:hover": {
                  bgcolor: "secondary.600",
                },
              },
            },
          }}
        >
          <ToggleButton value="all" aria-label="Ver todos los productos">
            Todos
          </ToggleButton>
          <ToggleButton
            value="differences"
            aria-label="Ver productos con diferencias"
          >
            Con Diferencias
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Buscar"
          size="small"
          disabled={disabled}
          inputProps={{
            maxLength: 200,
            "aria-label": "Buscar productos del conteo",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "primary.900", fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Limpiar búsqueda"
                  onClick={() => onSearchChange("")}
                >
                  <ClearIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            flex: { xs: 1, lg: "0 0 auto" },
            minWidth: 0,
            width: { xs: "auto", lg: 260 },
            "& .MuiOutlinedInput-root": {
              ...fieldRootSx,
              borderRadius: "10px",
              boxShadow: "none",
              "& fieldset": { borderColor: "neutral.700" },
              "&:hover fieldset": { borderColor: "primary.900" },
            },
          }}
        />
      </Stack>
    </Stack>
  );
}
